import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, timer, throwError, TimeoutError } from 'rxjs';
import { retry, timeout } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { CenterService } from '../../../core/services/center.service';
import { LabelPrintService } from '../../../core/services/label-print.service';
import { PageTitleService } from '../../../core/services/page-title.service';
import { VioTablesV2Module, VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { BARCODE_TABLE_BASE } from '../table-configs';
import { DateFormatService } from '../../../core/services/date-format.service';

@Component({
  selector: 'app-barcode-labels',
  standalone: true,
  imports: [CommonModule, FormsModule, VioTablesV2Module],
  templateUrl: './barcode-labels.component.html',
  styleUrl: './barcode-labels.component.scss',
})
export class BarcodeLabelsComponent implements OnInit {

  allRecords: any[] = [];
  isListLoading = false;
  loadError = '';
  private readonly LOAD_TIMEOUT_MS = 20000;

  // ── Zebra printer discovery ──────────────────────────────────
  zebraPrinters: any[] = [];
  selectedPrinterUid = '';
  isPrintersLoading = false;
  printersError = '';

  // ── Per-print field toggles ───────────────────────────────────
  showClientName = true;
  private _resolvedClientName = '';

  get clientNameStr(): string { return this._resolvedClientName; }

  get selectedDevice(): any | null {
    if (!this.selectedPrinterUid || !this.zebraPrinters.length) return null;
    return this.zebraPrinters.find(p => p.uid === this.selectedPrinterUid) ?? null;
  }

  tableConfig: VT2_TABLE_CONFIG = {
    ...BARCODE_TABLE_BASE,
    actionButtons: [
      { icon: '🖨', title: 'Print Label', cssClass: '', onClick: (row) => this.openModal(row) },
      { icon: '🔁', title: 'Reprint',     cssClass: '', onClick: (row) => this.openReprintModal(row) },
    ],
  };

  // Generate modal state
  showModal     = false;
  selectedBook: any = null;
  copies        = 1;
  step: 'input' | 'result' = 'input';
  isGenerating  = false;
  generatedSrNos: number[] = [];
  errorMessage  = '';
  printFailReason = '';

  // Reprint modal state
  showReprintModal   = false;
  reprintBook: any   = null;
  inventorySrNos: number[] = [];
  isLoadingInventory = false;
  reprintFailReason = '';

  constructor(
    private api:        ApiService,
    private auth:       AuthService,
    private centerSvc:  CenterService,
    private printSvc:   LabelPrintService,
    private pageTitle:  PageTitleService,
    private dateFmt:    DateFormatService,
  ) {}

  ngOnInit(): void {
    this.tableConfig = { ...this.tableConfig, dateDisplayFormat: this.dateFmt.current };
    this.pageTitle.set('Print Barcode Labels', 'Generate and print barcode labels for book copies');
    this.loadBooks();
    this.refreshPrinters();
    this.printSvc.resolveClientName().then(name => this._resolvedClientName = name);
  }

  async refreshPrinters(): Promise<void> {
    this.isPrintersLoading = true;
    this.printersError = '';
    try {
      this.zebraPrinters = await this.printSvc.getAvailablePrinters();
      if (this.zebraPrinters.length > 0) {
        const still = this.zebraPrinters.find(p => p.uid === this.selectedPrinterUid);
        this.selectedPrinterUid = still ? still.uid : this.zebraPrinters[0].uid;
      } else {
        this.selectedPrinterUid = '';
        this.printersError = 'No Zebra printers found — is Zebra Browser Print running?';
      }
    } catch {
      this.zebraPrinters = [];
      this.selectedPrinterUid = '';
      this.printersError = 'Could not reach Zebra Browser Print service.';
    }
    this.isPrintersLoading = false;
  }

  // Retries transient failures (timeouts, dropped connections, 5xx) — mirrors
  // book-master.component's loadList retry so a blip doesn't surface as an error.
  private _retryTransient() {
    return retry<any>({
      count: 2,
      delay: (err: any) => {
        const retryable = err instanceof TimeoutError || err instanceof HttpErrorResponse;
        return retryable ? timer(400) : throwError(() => err);
      },
    });
  }

  async loadBooks(): Promise<void> {
    this.isListLoading = true;
    this.loadError = '';
    try {
      const data = await firstValueFrom(
        this.api.getBooks({ isActive: 'true' }).pipe(timeout(this.LOAD_TIMEOUT_MS), this._retryTransient())
      );
      this.allRecords = data || [];
    } catch (err: any) {
      this.allRecords = [];
      this.loadError = err?.message || 'Failed to load books — please check your connection and try again.';
    } finally {
      this.isListLoading = false;
    }
  }

  openModal(book: any): void {
    this.selectedBook   = book;
    this.copies         = 1;
    this.step           = 'input';
    this.generatedSrNos = [];
    this.errorMessage   = '';
    this.printFailReason = '';
    this.showModal      = true;
  }

  closeModal(): void { this.showModal = false; }

  openReprintModal(book: any): void {
    this.reprintBook        = book;
    this.inventorySrNos     = [];
    this.isLoadingInventory = true;
    this.reprintFailReason  = '';
    this.showReprintModal   = true;
    this.api.getBookInventory(book._id).subscribe({
      next:  (srNos) => { this.inventorySrNos = srNos; this.isLoadingInventory = false; },
      error: ()      => { this.isLoadingInventory = false; },
    });
  }

  closeReprintModal(): void { this.showReprintModal = false; }

  async reprintSingle(srNo: number): Promise<void> {
    this.reprintFailReason = '';
    const device = this.selectedDevice ?? undefined;
    const result = await this.printSvc.print(this.reprintBook?.bookName || '', [srNo], this.showClientName ? this.clientNameStr : '', device);
    if (!result.printed) this.reprintFailReason = result.reason || 'Print failed — a .zpl file was downloaded instead.';
  }

  get selectedCenter(): any { return this.centerSvc.selectedCenter; }
  get isAdmin(): boolean { return this.auth.getCurrentUser()?.role === 'admin'; }

  generateLabels(): void {
    if (!this.copies || this.copies < 1 || this.copies > 200) return;

    this.isGenerating = true;
    this.errorMessage = '';

    this.api.generateLabels({
      bookId:   this.selectedBook._id,
      copies:   this.copies,
      centerId: this.selectedCenter?._id || null,
    }).subscribe({
      next: (result: any) => {
        this.generatedSrNos = (result.generatedSrNos || []).map((n: any) => Number(n));
        this.step           = 'result';
        this.isGenerating   = false;
        if (this.selectedBook && result.newCopiesAvailable != null) {
          this.selectedBook = { ...this.selectedBook, copiesAvailable: result.newCopiesAvailable };
        }
        this.loadBooks();
      },
      error: (err: any) => {
        this.errorMessage = err?.message || 'Failed to generate labels. Please try again.';
        this.isGenerating = false;
      },
    });
  }

  async printLabels(): Promise<void> {
    if (!this.generatedSrNos.length) return;
    this.printFailReason = '';
    const device = this.selectedDevice ?? undefined;
    const result = await this.printSvc.print(this.selectedBook?.bookName || '', this.generatedSrNos, this.showClientName ? this.clientNameStr : '', device);
    if (!result.printed) this.printFailReason = result.reason || 'Print failed — a .zpl file was downloaded instead.';
  }
}
